import { createFileRoute, Link } from "@tanstack/react-router";
import { HiddenSeoContext } from "@/components/site/HiddenSeoContext";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ArrowRight, Loader2, BookOpen, Calendar, Percent, Receipt, ShieldCheck, Globe, Landmark } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { SectionHeading } from "@/components/site/SectionHeading";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fetchBlogPosts } from "@/lib/content";
import { optimizedImg, optimizedSrcSet } from "@/lib/img-url";
import { SeoFaqBlock } from "@/components/site/SeoFaqBlock";
import { SEO_CONTENT } from "@/components/site/seo-content";
import { ServiceDetailSection } from "@/components/site/ServiceDetailSection";
import { SERVICE_DETAIL_CONTENT } from "@/lib/service-detail-content";
import { breadcrumbScript } from "@/lib/breadcrumb-jsonld";
import { webPageScript } from "@/lib/webpage-jsonld";

const BLOG_BULLET_ICONS = [
  { Icon: Percent, className: "bg-sky-500 text-white" },
  { Icon: Receipt, className: "bg-violet-500 text-white" },
  { Icon: ShieldCheck, className: "bg-cyan-600 text-white" },
  { Icon: Globe, className: "bg-amber-500 text-white" },
  { Icon: Landmark, className: "bg-red-600 text-white" },
];

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Income Tax & VAT Blog Bangladesh | NBR e-Return & Mushak Guides" },
      { name: "description", content: "Expert NBR-certified guides on income tax return filing, online e-Return, e-TIN registration, VAT registration (BIN), Mushak 9.1 monthly VAT return and business tax compliance in Bangladesh." },
      { property: "og:title", content: "Income Tax & VAT Blog — Online Tax VAT Bangladesh" },
      { property: "og:description", content: "Practical guides on Bangladesh income tax return, e-Return filing, e-TIN, VAT registration (BIN), Mushak 9.1 VAT return and NBR compliance for individuals and SMEs." },
      { property: "og:url", content: "https://onlinetaxvat.com/blog" },
    ],
    links: [{ rel: "canonical", href: "https://onlinetaxvat.com/blog" }],
    scripts: [breadcrumbScript([
      { name: "Home", path: "/" },
      { name: "Blog", path: "/blog" },
    ]), webPageScript({
      path: "/blog",
      name: "Tax & VAT Blog — Online Tax VAT Bangladesh",
      description: "Read practical tax and VAT guides for Bangladesh, including income tax returns, VAT registration, VAT returns and TIN support.",
    })],
  }),
  component: BlogPage,
});

function toTagArray(tags: any): string[] {
  if (!tags) return [];
  if (Array.isArray(tags)) return tags.map(String);
  if (typeof tags === "string") {
    try { const p = JSON.parse(tags); return Array.isArray(p) ? p.map(String) : []; } catch { return tags.split(",").map(s => s.trim()).filter(Boolean); }
  }
  return [];
}

function BlogPage() {
  const { t, i18n } = useTranslation();
  const isBn = (i18n.resolvedLanguage || i18n.language || "en").startsWith("bn");
  const { data: posts = [], isLoading } = useQuery({ queryKey: ["blog_posts"], queryFn: fetchBlogPosts });

  return (
    <SiteShell>
      <section className="pt-14 md:pt-20 pb-20">
        <div className="container-page">
          <h1 className="sr-only">Income Tax, Tax Return &amp; VAT Blog — Bangladesh</h1>
          <p className="sr-only">Practical, NBR-aligned articles on income tax in Bangladesh, tax return preparation, filing the income tax return form online, VAT registration and compliance — written for individuals, freelancers and business owners.</p>

          <div
            className="blog-grid-slot"
            style={{ minHeight: 900 }}
          >

          {isLoading ? (
            <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
          ) : posts.length === 0 ? (
            <p className="text-center py-20 text-muted-foreground">{t("blogPage.empty")}</p>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p: any, i: number) => {
                const tags = toTagArray(p.tags);
                const title = isBn && p.title_bn ? p.title_bn : p.title;
                const excerpt = isBn && p.excerpt_bn ? p.excerpt_bn : p.excerpt;
                return (
                  <article
                    key={p.id}
                    className="glass rounded-3xl overflow-hidden shadow-card flex flex-col group hover:shadow-glow transition-shadow"
                    style={{ minHeight: 420, contain: "layout" }}
                  >

                    {p.cover_url ? (
                      <div className="relative w-full overflow-hidden bg-muted" style={{ aspectRatio: "16 / 9" }}>
                        <img
                          src={optimizedImg(p.cover_url, { width: 960, quality: 78, resize: "contain" })}
                          srcSet={optimizedSrcSet(p.cover_url, [480, 720, 960], { quality: 78, resize: "contain" })}
                          sizes="(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw"
                          alt={`Online VAT and Tax Latest Update Bangladesh — ${title}`}
                          loading="lazy"
                          decoding="async"
                          width={960}
                          height={540}
                          className="absolute inset-0 w-full h-full object-contain object-center"
                        />
                      </div>
                    ) : (
                      <div className="relative w-full overflow-hidden gradient-primary flex items-center justify-center" style={{ aspectRatio: "16 / 9" }}>
                        <BookOpen className="h-12 w-12 text-primary-foreground/80" />
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <div className="flex flex-wrap gap-2 mb-3 min-h-[28px]">
                        {tags.slice(0, 3).map((tg) => (
                          <Badge key={tg} variant="secondary" className="bg-primary/10 text-primary border-primary/20 hover:bg-primary/15">{tg}</Badge>
                        ))}
                      </div>
                      <h2 className="font-display font-bold text-xl leading-snug group-hover:text-primary transition-colors line-clamp-2 min-h-[56px]">
                        <Link to="/blog/$slug" params={{ slug: p.slug }} reloadDocument>{title}</Link>
                      </h2>
                      {excerpt && <p className="mt-3 text-sm text-muted-foreground line-clamp-3 flex-1">{excerpt}</p>}
                      <div className="mt-5 flex items-center justify-between">
                        {p.published_at && (
                          <span className="text-xs text-muted-foreground inline-flex items-center gap-1.5">
                            <Calendar className="h-3.5 w-3.5" />
                            {new Date(p.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
                          </span>
                        )}
                        <Button asChild variant="ghost" size="sm" className="text-primary hover:bg-primary/10">
                          <Link to="/blog/$slug" params={{ slug: p.slug }} reloadDocument>{t("blogPage.readMore")} <ArrowRight className="ml-1 h-3.5 w-3.5" /></Link>
                        </Button>
                      </div>

                    </div>
                  </article>
                );
              })}
            </div>
          )}
          </div>

          {isBn ? (
            <div className="mt-20 max-w-3xl mx-auto text-muted-foreground leading-relaxed">
              <h1 className="text-3xl font-bold mb-4 text-foreground text-center">ট্যাক্স ও ভ্যাট তথ্য ভাণ্ডার</h1>
              <p className="font-medium mb-4 text-center">
                বাংলাদেশে আয়কর, ভ্যাট, BIN, TIN এবং ব্যবসায়িক কমপ্লায়েন্স বিষয়ক বিশেষজ্ঞ গাইড ও আপডেট।
              </p>
              <p>
                অনলাইন ট্যাক্স ভ্যাট বাংলাদেশ ব্লগে আপনাকে স্বাগতম — এটি করদাতা, ব্যবসায়ী, ফ্রিল্যান্সার, ই-কমার্স বিক্রেতা এবং ফাইন্যান্স টিমের জন্য জাতীয় রাজস্ব বোর্ডের (NBR) নিয়মকানুন সহজভাবে বোঝার একটি বাস্তবসম্মত জ্ঞানভাণ্ডার। এখানে আপনি আয়কর রিটার্ন দাখিল, ই-টিন (e-TIN) রেজিস্ট্রেশন, ভ্যাট রেজিস্ট্রেশন (BIN), প্রতি মাসের মূসক ৯.১ সাবমিশন, ট্রেড লাইসেন্স নবায়ন, কোম্পানির কমপ্লায়েন্স এবং ভিসা ট্যাক্স ফাইলের ওপর ধাপে ধাপে গাইড পাবেন — যা এনবিআর-প্রত্যয়িত উপদেষ্টাদের দ্বারা অত্যন্ত সহজ ভাষায় লেখা এবং পুঙ্খানুপুঙ্খভাবে পর্যালোচিত।
              </p>
              <p className="mt-3">
                অর্থ আইন (Finance Act), এসআরও (SROs), বাজেটের পরিবর্তন বা এনবিআর ই-রিটার্ন পোর্টালে নতুন কোনো নিয়ম চালু হলেই আমরা প্রতিটি আর্টিকেল তাৎক্ষণিকভাবে আপডেট করি, যাতে আপনি সবসময় একদম বর্তমান প্রক্রিয়াটিই জানতে পারেন। ডেডলাইনের চেয়ে এগিয়ে থাকতে, আপনার প্রাপ্য প্রতিটি কর রেয়াত (Rebate) সঠিকভাবে ক্লেইম করতে এবং এনবিআর-এর কোয়েরি নোটিশ এড়াতে এই পেজটি বুকমার্ক করে রাখুন।
              </p>

              <h2 className="text-xl font-semibold mt-6 mb-2 text-foreground">আমাদের ব্লগে কী কী গাইড পাবেন?</h2>
              <p>
                আমাদের প্রতিটি আর্টিকেল এনবিআর-অভিজ্ঞ কনসালট্যান্টদের দ্বারা তৈরি, যেখানে আপনি পাবেন ভ্যাট রেজিস্ট্রেশন, মূসক ৬.৩ চালানপত্র ব্যবস্থাপনা, ডিএসসিসি (DSCC) ও ডিএনসিসি (DNCC)-সহ বিভিন্ন সিটি কর্পোরেশনের ট্রেড লাইসেন্স ইস্যু, আরজেএসসি (RJSC) কোম্পানির ফাইলিং, বিডা (BIDA) অনুমোদন এবং অ্যাম্বাসিগুলোর জন্য ভিসা ট্যাক্স ফাইলের গভীর বিশ্লেষণ। পাঠকদের আত্মবিশ্বাসের সাথে কাজ করার সুবিধার্থে প্রতিটি গাইডে ধাপে ধাপে স্ক্রিনশট, প্রয়োজনীয় ডকুমেন্টের চেকলিস্ট এবং সাধারণ ভুলের সমাধান অন্তর্ভুক্ত রয়েছে।
              </p>
              <p className="mt-2">
                সামনে আসছে ২০২৬-২০২৭ কর বছরের রিটার্ন সাইকেল, ২০২৬ সালের অনলাইন আয়কর দাখিল প্রক্রিয়া, ভ্যাট রেজিস্ট্রেশনের সর্বশেষ পরিবর্তন এবং ফ্রিল্যান্সার ও এসএমইদের জন্য বিশেষ ট্যাক্স প্ল্যানিং গাইড!
              </p>

              <h2 className="text-xl font-semibold mt-6 mb-2 text-foreground">টপিক অনুযায়ী ব্রাউজ করুন — কর ও ভ্যাট জ্ঞান</h2>
              <p className="text-sm mb-3">
                আমাদের সকল আর্টিকেল প্র্যাকটিসিং চার্টার্ড অ্যাকাউন্ট্যান্ট এবং কর আইনজীবীদের দ্বারা লিখিত। সর্বশেষ অর্থ আইন অনুযায়ী প্রতি অর্থবছর এগুলো আপডেট করা হয়।
              </p>
              <ul className="space-y-3">
                {[
                  { label: "আয়কর (Income Tax):", text: "ট্যাক্স স্ল্যাব, রেয়াত/কর ছাড়, এক্সেম্পশন এবং ডেডলাইন গাইড" },
                  { label: "ভ্যাট ও সম্পূরক শুল্ক (VAT & SD):", text: "ভ্যাট নিবন্ধন, মাসিক রিটার্ন দাখিল এবং অডিট ডিফেন্স" },
                  { label: "কোম্পানি কমপ্লায়েন্স:", text: "বার্ষিক রিটার্ন সাবমিশন, অডিট এবং ট্রান্সফার প্রাইসিং" },
                  { label: "ভিসা ট্যাক্স ফাইল:", text: "প্রতিটি দেশের অ্যাম্বাসি অনুযায়ী আলাদা প্রয়োজনীয় ডকুমেন্টের গাইড" },
                  { label: "বাংলাদেশ অর্থ আইন:", text: "বছর-ভিত্তিক ট্যাক্স ও ভ্যাট আইনের পরিবর্তন লোগ" },
                ].map((item, i) => {
                  const { Icon, className } = BLOG_BULLET_ICONS[i];
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <span className={`${className} p-1.5 rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0 shadow-sm`}>
                        <Icon className="h-4 w-4" strokeWidth={2.25} />
                      </span>
                      <span className="pt-1"><strong className="text-foreground">{item.label}</strong> {item.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <>
              <div className="mt-20">
                <SectionHeading
                  eyebrow={t("blogPage.eyebrow")}
                  title={<>{t("blogPage.titleA")}<span className="gradient-text">{t("blogPage.titleB")}</span></>}
                  description={t("blogPage.desc")}
                />

                <div className="mt-8 max-w-3xl mx-auto text-center text-sm md:text-base leading-relaxed text-muted-foreground space-y-3">
                  <p>
                    Welcome to the Online Tax VAT Bangladesh blog — a practical knowledge base for individual
                    taxpayers, business owners, freelancers, e-commerce sellers and finance teams working with the
                    National Board of Revenue (NBR). You will find step-by-step guides on income tax return filing
                    in Bangladesh, online e-Return submission, e-TIN registration, VAT registration (BIN), monthly
                    Mushak 9.1 VAT return, trade license renewal, corporate tax compliance and visa tax file
                    preparation — written and reviewed by NBR-certified tax consultants in clear English and Bangla.
                  </p>
                  <p>
                    We update every guide whenever the Finance Act, SROs, national budget or the NBR e-Return
                    portal introduce new rules, so you always read the current process — not last year's version.
                    Bookmark this tax blog to stay ahead of deadlines, calculate your income tax accurately, claim
                    every tax rebate you are entitled to and avoid the common mistakes that trigger NBR query
                    letters or VAT audit notices.
                  </p>
                </div>
              </div>

              <div className="mt-16 max-w-3xl mx-auto text-center text-sm leading-relaxed text-muted-foreground space-y-3">
                <p>
                  The Online Tax VAT Bangladesh blog is a practitioner-focused publication for taxpayers, business
                  owners, freelancers, e-commerce sellers and finance teams operating under the National Board of
                  Revenue (NBR). Every article is written or reviewed by NBR-certified income tax and VAT
                  consultants and is refreshed whenever circulars, SROs, Finance Act amendments or upgrades on the
                  NBR e-Return and iVAS portals affect compliance procedures in Bangladesh.
                </p>
                <p>
                  Expect deep-dive guides on VAT registration in Bangladesh (BIN), monthly VAT return filing
                  (Mushak 9.1), e-TIN certificate issuance and recovery, personal and corporate income tax return
                  preparation, trade license issuance and renewal across DSCC, DNCC and other city corporations,
                  IRC and ERC import-export registration, RJSC company filings, BIDA approvals and visa tax files.
                  Every guide includes step-by-step screenshots, document checklists, government fees, deadlines
                  and common rejection reasons so readers can act with confidence.
                </p>
                <p>
                  Upcoming articles cover the Income Tax Return 2026-2027 cycle, online income tax filing in 2026,
                  latest VAT rate and BIN registration changes, tax audit readiness, tax planning and rebate
                  strategies for SMEs and freelancers, and answers to the most common questions our tax advisors
                  receive on WhatsApp and email — all in clear English and Bangla.
                </p>
              </div>
            </>
          )}

          {/* TIN Certificate SEO content block */}
          <section
            aria-labelledby="tin-certificate-guide"
            className="mt-16 max-w-3xl mx-auto text-muted-foreground leading-relaxed"
          >
            {isBn ? (
              <>
                <h2 id="tin-certificate-guide" className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
                  TIN Certificate বাংলাদেশ — e-TIN রেজিস্ট্রেশন, লগইন ও ডাউনলোড গাইড
                </h2>
                <p>
                  <strong>TIN (Taxpayer Identification Number)</strong> হলো জাতীয় রাজস্ব বোর্ড (NBR) কর্তৃক প্রদত্ত ১২-সংখ্যার একটি ইউনিক করদাতা শনাক্তকরণ নম্বর। বাংলাদেশে ব্যাংক অ্যাকাউন্ট খোলা, ট্রেড লাইসেন্স ইস্যু, গাড়ি রেজিস্ট্রেশন, জমি কেনাবেচা, ক্রেডিট কার্ড আবেদন এবং সরকারি টেন্ডারে অংশগ্রহণের জন্য <strong>e-TIN certificate</strong> এখন বাধ্যতামূলক। আমাদের এই গাইডে আপনি পাবেন <em>TIN registration</em>, <em>TIN certificate registration</em>, <em>TIN certificate login</em> এবং সার্টিফিকেট ডাউনলোডের সম্পূর্ণ প্রক্রিয়া।
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-2 text-foreground">e-TIN Registration কীভাবে করবেন?</h3>
                <p>
                  NBR-এর অফিসিয়াল ওয়েবসাইট <span className="font-mono">secure.incometax.gov.bd</span>-এ গিয়ে "Register" বাটনে ক্লিক করে ইউজার আইডি, পাসওয়ার্ড এবং মোবাইল নম্বর দিয়ে অ্যাকাউন্ট তৈরি করতে হবে। এরপর NID/পাসপোর্ট তথ্য, ঠিকানা ও ব্যবসার ধরন পূরণ করে সাবমিট করলে সাথে সাথে PDF ফরম্যাটে <strong>TIN certificate</strong> জেনারেট হয়ে যায় — কোনো ফি লাগে না।
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-2 text-foreground">TIN Certificate Login ও ডাউনলোড</h3>
                <p>
                  পুরাতন TIN হোল্ডাররা একই পোর্টালে ইউজার আইডি ও পাসওয়ার্ড দিয়ে <strong>TIN certificate login</strong> করতে পারবেন। ড্যাশবোর্ড থেকে "View Certificate" অপশনে ক্লিক করে যে কোনো সময় সার্টিফিকেট রি-ডাউনলোড বা প্রিন্ট করা যাবে। পাসওয়ার্ড হারালে "Forgot Password" থেকে রেজিস্টার্ড মোবাইলে OTP নিয়ে রিসেট করুন।
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-2 text-foreground">TIN থাকলেই কি রিটার্ন দাখিল বাধ্যতামূলক?</h3>
                <p>
                  হ্যাঁ। ২০২৩ সালের অর্থ আইন অনুযায়ী TIN হোল্ডারদের জন্য বার্ষিক income tax return দাখিল এবং <strong>Proof of Submission of Return (PSR)</strong> সংগ্রহ করা বাধ্যতামূলক। রিটার্ন না দিলে ব্যাংক লেনদেন, গাড়ি ফিটনেস, ট্রেড লাইসেন্স নবায়নসহ ৪৩টি সেবায় সমস্যা হবে। আমাদের অভিজ্ঞ কনসালট্যান্ট টিম সহজেই আপনার e-TIN registration ও রিটার্ন দাখিল সম্পন্ন করে দিতে পারে।
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/services" className="inline-flex items-center px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition">
                    e-TIN সার্ভিস দেখুন →
                  </Link>
                  <Link to="/contact" className="inline-flex items-center px-5 py-2.5 rounded-full border border-emerald-600 text-emerald-700 dark:text-emerald-300 text-sm font-semibold hover:bg-emerald-50 dark:hover:bg-emerald-950 transition">
                    বিনামূল্যে পরামর্শ নিন
                  </Link>
                </div>
              </>
            ) : (
              <>
                <h2 id="tin-certificate-guide" className="text-2xl md:text-3xl font-bold mb-4 text-foreground">
                  TIN Certificate in Bangladesh — Complete Guide to e-TIN Registration, Login & Download
                </h2>
                <p>
                  A <strong>TIN certificate (Taxpayer Identification Number)</strong> is a 12-digit unique identifier issued by the National Board of Revenue (NBR) to every registered taxpayer in Bangladesh. Today, an <strong>e-TIN certificate</strong> is mandatory for opening a bank account, obtaining a trade license, registering a vehicle, buying or selling land, applying for a credit card and participating in government tenders. This guide walks you through <em>TIN registration</em>, <em>TIN certificate registration</em>, <em>TIN certificate login</em> and downloading your certificate — the right way.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-2 text-foreground">How to Complete e-TIN Registration Online</h3>
                <p>
                  Visit the NBR portal at <span className="font-mono">secure.incometax.gov.bd</span>, click <strong>Register</strong>, and create an account using a user ID, password and active mobile number. Enter your NID or passport details, present address and business type, then submit. Your <strong>TIN certificate</strong> is generated instantly as a downloadable PDF — completely free of cost.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-2 text-foreground">TIN Certificate Login & Re-download</h3>
                <p>
                  Existing TIN holders can perform a <strong>TIN certificate login</strong> on the same NBR portal using their user ID and password. From the dashboard, click <em>View Certificate</em> to reprint or re-download the certificate anytime. If you have lost your password, use <em>Forgot Password</em> to reset it via an OTP sent to your registered mobile number.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-2 text-foreground">Is Filing a Return Mandatory After TIN Registration?</h3>
                <p>
                  Yes. Under the Finance Act 2023, every TIN holder must file an annual income tax return and collect a <strong>Proof of Submission of Return (PSR)</strong>. Without a PSR, 43 government services — including bank transactions, vehicle fitness, and trade license renewal — will be restricted. Our NBR-certified consultants can handle your entire <strong>e-TIN registration</strong>, return filing and PSR collection quickly and correctly.
                </p>

                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/services" className="inline-flex items-center px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition">
                    View e-TIN Services →
                  </Link>
                  <Link to="/contact" className="inline-flex items-center px-5 py-2.5 rounded-full border border-emerald-600 text-emerald-700 dark:text-emerald-300 text-sm font-semibold hover:bg-emerald-50 dark:hover:bg-emerald-950 transition">
                    Get Free Consultation
                  </Link>
                </div>
              </>
            )}
          </section>



          {(() => {
            const c = SEO_CONTENT["blog"];
            if (isBn) {
              return (
                <SeoFaqBlock
                  title="সাধারণ জিজ্ঞাসা (FAQ)"
                  faqs={[
                    { q: "আপনারা কত ঘন ঘন নতুন আর্টিকেল প্রকাশ করেন?", a: "এনবিআর (NBR) যখনই নতুন কোনো সার্কুলার, অর্থ আইন বা এসআরও (SRO) জারি করে, আমরা সাথে সাথে গাইডগুলো আপডেট করি। এছাড়া প্রতি সপ্তাহে নিয়মিতভাবে নতুন গাইড ও কাস্টমারদের সাধারণ প্রশ্নগুলোর সমাধান ব্লগে যুক্ত করা হয়।" },
                    { q: "আমি কি নির্দিষ্ট কোনো বিষয়ের ওপর আর্টিকেলের জন্য অনুরোধ করতে পারি?", a: "অবশ্যই! ট্যাক্স বা ভ্যাট সংক্রান্ত কোনো বিষয়ে আপনার গাইড প্রয়োজন হলে আমাদের সাপোর্ট ইমেইলে বা সরাসরি হোয়াটসঅ্যাপে জানাতে পারেন। আমাদের এক্সপার্ট টিম দ্রুত সেই বিষয়ের ওপর বিস্তারিত গাইড প্রকাশ করবে।" },
                    { q: "এই আর্টিকেলগুলো কি আইনি পরামর্শ হিসেবে গণ্য হবে?", a: "ব্লগের আর্টিকেলগুলো মূলত সাধারণ সচেতনতা ও করদাতাদের সহজে গাইড করার উদ্দেশ্যে তৈরি। যেহেতু প্রতিটি ব্যবসা ও ব্যক্তির করের ক্ষেত্র আলাদা, তাই চূড়ান্ত আইনি বা প্রফেশনাল সিদ্ধান্তের জন্য আমাদের ড্যাশবোর্ড থেকে সরাসরি কনসালট্যান্টের পরামর্শ নেওয়ার অনুরোধ করা হলো।" },
                    { q: "আপনারা কি গেস্ট রাইটার বা অতিথি লেখকদের কন্টেন্ট গ্রহণ করেন?", a: "তথ্যের নির্ভুলতা ও এনবিআর কমপ্লায়েন্স শতভাগ বজায় রাখার জন্য আমাদের ব্লগের প্রতিটি লেখা শুধুমাত্র প্রত্যয়িত চার্টার্ড অ্যাকাউন্ট্যান্ট, ট্যাক্স আইনজীবী এবং আমাদের নিজস্ব প্যানেল বিশেষজ্ঞদের দ্বারাই তৈরি ও পর্যালোচিত হয়ে থাকে।" },
                  ]}
                />
              );
            }
            return <SeoFaqBlock title={c.title} intro={c.intro} bullets={c.bullets} bulletIcons={BLOG_BULLET_ICONS} faqs={c.faqs} />;
          })()}
          <ServiceDetailSection content={SERVICE_DETAIL_CONTENT["blog"]} />
        </div>
      </section>
      <HiddenSeoContext topic="blog" />
    </SiteShell>
  );
}
